import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Row, Tooltip, Typography } from 'antd'
import { ArrowDownOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { getActivationMetrics, MetricsPeriod } from '../../../Actions/operationsMetricsApi'
import { formatHours, formatMinutes, formatNumber, formatRate } from './formatters'

interface Props {
  period: MetricsPeriod
  windowDays: number
  paidWindowDays: number
}

interface Stage {
  label: string
  value: number
  color: string
  /** 직전 단계명 — 단계 간 전환율 설명용 */
  fromLabel?: string
}

/**
 * 📈 가입 퍼널 — 신규가입 → 첫 영상(활성화) → 유료전환.
 * 사다리꼴 대신 가로 막대로 그림(낙폭이 커도 안 망가지고 단계별/단계간 전환율을 함께 표기).
 * 세 숫자 모두 분모가 signups로 동일한 코호트.
 */
export default function FunnelSection({ period, windowDays, paidWindowDays }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'activation', period, windowDays, paidWindowDays],
    queryFn: () => getActivationMetrics({ ...period, windowDays, paidWindowDays }),
  })

  const stages: Stage[] = useMemo(
    () =>
      data
        ? [
            { label: '가입', value: data.signups, color: '#8c8c8c' },
            { label: `첫 영상 (${windowDays}일)`, value: data.activatedWithinWindow, color: '#3f8600', fromLabel: '가입' },
            {
              label: `유료전환 (${paidWindowDays}일)`,
              value: data.convertedToPaidWithinWindow,
              color: '#1677ff',
              fromLabel: '첫 영상',
            },
          ]
        : [],
    [data, windowDays, paidWindowDays],
  )

  if (error)
    return <Alert type="error" message="퍼널 지표를 불러오지 못했습니다." description={(error as Error).message} />

  const base = data?.signups || 0

  // 분 우선(백엔드 전환 후) → 없으면 시간 절삭본 폴백(0은 "1시간 미만"으로 정직하게)
  const m = data?.minutesToFirstVideo
  const h = data?.hoursToFirstVideo
  const hourLabel = (v: number | null | undefined) => (v == null ? '—' : v < 1 ? '1시간 미만' : formatHours(v))
  const timeStat = m
    ? { p50: formatMinutes(m.p50Minutes), p90: formatMinutes(m.p90Minutes), approx: false }
    : h
    ? { p50: hourLabel(h.p50Hours), p90: hourLabel(h.p90Hours), approx: true }
    : null

  return (
    <Card title="📈 가입 퍼널 (가입 → 첫 영상 → 유료전환 · 가입 코호트 기준)" size="small" loading={isLoading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          {stages.map((stage, i) => {
            const overall = base > 0 ? stage.value / base : 0
            const prev = i > 0 ? stages[i - 1]?.value ?? 0 : 0
            const step = i > 0 && prev > 0 ? stage.value / prev : null
            return (
              <div key={stage.label}>
                {i > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, paddingLeft: 8 }}>
                    <ArrowDownOutlined /> {step != null ? formatRate(step) : '—'}
                    <span style={{ color: '#bbb' }}> ({stage.fromLabel} 대비)</span>
                  </Typography.Text>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                  <span style={{ width: 110, flexShrink: 0, fontSize: 13 }}>{stage.label}</span>
                  <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 28, position: 'relative' }}>
                    <div
                      style={{
                        width: `${Math.max(overall * 100, base > 0 && stage.value > 0 ? 1 : 0)}%`,
                        background: stage.color,
                        height: '100%',
                        borderRadius: 4,
                        transition: 'width 0.3s',
                      }}
                    />
                  </div>
                  <span style={{ width: 130, flexShrink: 0, textAlign: 'right', fontSize: 13 }}>
                    <strong>{formatNumber(stage.value)}</strong>
                    <span style={{ color: '#999' }}> · {formatRate(overall)}</span>
                  </span>
                </div>
              </div>
            )
          })}
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Typography.Text type="secondary">
              가입 → 첫 영상 요청까지{' '}
              <Tooltip title="영상 '생성 요청(버튼 클릭)' 시각 기준 — 렌더 완료 시각 아님">
                <InfoCircleOutlined style={{ color: '#bbb' }} />
              </Tooltip>
            </Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
              {timeStat ? (
                <>
                  p50 <strong>{timeStat.p50}</strong>
                  <br />
                  p90 <strong>{timeStat.p90}</strong>
                </>
              ) : (
                '—'
              )}
            </Typography.Paragraph>
            {timeStat?.approx && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                ※ 현재 시간 단위라 1시간 미만은 0으로 절삭됨 (분 단위 적용 예정)
              </Typography.Text>
            )}
          </Card>
        </Col>
      </Row>
    </Card>
  )
}
